import type { UIColorTokens } from '@/core/constants/uiColors';
import { useI18nContext, useUIColors } from '@/core/hooks';
import React from 'react';
import { Platform, StyleSheet, Text, TextProps } from 'react-native';

// Typography component types based on Figma design system
export type TypographyVariant =
    | 'display'       // 64px - Display/Hero title
    | 'largeTitle'    // 34px - Large title
    | 'title1'        // 28px - Title 1 
    | 'title2'        // 22px - Title 2
    | 'title3'        // 20px - Title 3
    | 'headlineLarge' // 18px semibold - Headline Large
    | 'headline'      // 16px semibold - Headline
    | 'body'          // 16px regular - Body
    | 'bodyLarge'     // 18px regular - Body Large
    | 'callout'       // 15px regular - Callout (center aligned)
    | 'subhead'       // 14px medium - Subhead
    | 'footnote'      // 13px regular - Footnote
    | 'caption';      // 12px regular - Caption

export interface TypographyProps extends Omit<TextProps, 'style'> {
    variant?: TypographyVariant;
    color?: 'primary' | 'secondary' | 'tertiary' | 'highContrast' | 'error' | 'success' | 'white' | keyof UIColorTokens | string;
    style?: TextProps['style'];
}

export const Typography = React.memo<TypographyProps>(function Typography({
    variant = 'body',
    color = 'primary',
    style,
    children,
    ...props
}) {
    const colors = useUIColors();
    const { isRTL } = useI18nContext();

    const getColor = () => {
        switch (color) {
            case 'primary': return colors.fontPrimary;
            case 'secondary': return colors.fontSecondary;
            case 'tertiary': return colors.fontTertiary;
            case 'highContrast': return colors.fontHighContrast;
            case 'error': return colors.fontErrorText;
            case 'success': return colors.fontSuccessText;
            case 'white': return colors.white;
            default:
                // Check if it's a valid UIColorTokens key
                if (typeof color === 'string' && color in colors) {
                    return colors[color as keyof UIColorTokens];
                }
                // If it's not a predefined key or UIColorTokens key, treat it as a custom color string
                if (typeof color === 'string' && color !== 'primary') {
                    return color;
                }
                return colors.fontPrimary;
        }
    };

    // Get RTL-specific line height for variants that need it (body and headline)
    // lineHeight is 20px for Arabic (RTL) text to prevent overlap as suggested by marketing team
    const getRTLLineHeight = () => {
        if (!isRTL) return undefined;

        switch (variant) {
            case 'headline':
                return { lineHeight: 20 };
            case 'body':
                return { lineHeight: 24 };
            case 'title1':
                return { lineHeight: 32 };
            case 'title2':
                return { lineHeight: 28 };
            case 'subhead':
                return { lineHeight: 20 }; // 14px * ~1.43 for Arabic diacritics
            case 'footnote':
                return { lineHeight: 18 }; // 13px * ~1.38 for Arabic diacritics
            default:
                return undefined;
        }
    };

    const webTextAlign = isRTL ? 'right' : 'left';
    const computedTextAlign = Platform.OS === 'web' ? webTextAlign : 'left';

    return (
        <Text
            style={[
                styles.base,
                styles[variant],
                {
                    color: getColor(),
                    textAlign: computedTextAlign,
                },
                getRTLLineHeight(),
                style
            ]}
            {...props}
        >
            {children}
        </Text>
    );
});

const styles = StyleSheet.create({
    base: {
        fontFamily: 'Matter-Regular',
    },
    display: {
        fontSize: 64,
        fontFamily: 'Matter-SemiBold',
        fontWeight: '600',
        lineHeight: 64 * 1.2, // 76.8px - normal line height calculation
    },
    largeTitle: {
        fontSize: 34,
        fontFamily: 'Matter-Medium',
        fontWeight: '500', // Medium
        lineHeight: 34,
    },
    title1: {
        fontSize: 28,
        fontFamily: 'Matter-Medium',
        lineHeight: 28,
    },
    title2: {
        fontSize: 22,
        fontFamily: 'Matter-Medium',
        lineHeight: 22,
    },
    title3: {
        fontSize: 20,
        fontFamily: 'Matter-Medium',
        lineHeight: 20,
    },
    headlineLarge: {
        fontSize: 18,
        fontFamily: 'Matter-SemiBold',
        lineHeight: 18,
    },
    headline: {
        fontSize: 16,
        fontFamily: 'Matter-SemiBold',
        lineHeight: 16,
    },
    body: {
        fontSize: 16,
        fontFamily: 'Matter-Regular',
        lineHeight: 16,
    },
    bodyLarge: {
        fontSize: 18,
        fontFamily: 'Matter-Regular',
        fontWeight: '400',
        lineHeight: 18 * 1.2, // 21.6px - normal line height calculation
    },
    callout: {
        fontSize: 15,
        fontFamily: 'Matter-Regular',
        fontWeight: '400',
        lineHeight: 15, // normal line height
    },
    subhead: {
        fontSize: 14,
        fontFamily: 'Matter-Medium',
        lineHeight: 14,
    },
    footnote: {
        fontSize: 13,
        fontFamily: 'Matter-Regular',
        lineHeight: 13,
    },
    caption: {
        fontSize: 12,
        fontFamily: 'Matter-Regular',
        lineHeight: 12,
    },
});

export default Typography;
